// src/services/utils.js

// Centralized logging utility with different log levels
const isDevelopment = process.env.NODE_ENV !== 'production';
const isTest = process.env.NODE_ENV === 'test';

// Global flags to control logging in test environment
// These can be set in specific tests when more verbose logging is needed
if (isTest) {
  window.__LOG_CONFIG = {
    // Set to true to enable normal info logs during tests
    ENABLE_INFO_LOGS: false,
    // Set to true to enable error logs during tests
    ENABLE_ERROR_LOGS: false,
    // Set to true to enable debug logs during tests
    ENABLE_DEBUG_LOGS: false,
    // Set to true to enable service-specific logs during tests
    ENABLE_SERVICE_LOGS: false
  };
}

/**
 * Get CSRF token from cookie
 * Django requires this token for POST/PUT/PATCH/DELETE requests
 */
export const getCsrfToken = () => {
  const name = 'csrftoken=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');
  
  for (let cookie of cookieArray) {
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length);
    }
  }
  return '';
};

/**
 * Fetch a fresh CSRF token from the server
 * This should be called before making POST/PUT/PATCH/DELETE requests
 * when the user is not yet authenticated
 * 
 * @returns {Promise<boolean>} True if token was successfully fetched
 */
export const fetchCsrfToken = async () => {
  try {
    const response = await fetch('/accounts/api/csrf/', {
      method: 'GET',
      credentials: 'include'
    });
    logger.trace('Auth', 'Fetched CSRF token');
    return true;
  } catch (error) {
    logger.error('Auth', 'Failed to fetch CSRF token', error);
    return false;
  }
};

// Create logger functions with different severity levels
export const logger = {
  // Standard info log - critical info is shown in tests when enabled
  info: (prefix, ...args) => {
    if (!isTest || (isTest && window.__LOG_CONFIG?.ENABLE_INFO_LOGS)) {
      console.log(`[${prefix}]`, ...args);
    }
  },
  
  // Debug log - enabled only in development or when explicitly enabled in tests
  debug: (prefix, ...args) => {
    if ((isDevelopment && !isTest) || (isTest && window.__LOG_CONFIG?.ENABLE_DEBUG_LOGS)) {
      console.log(`[${prefix}][DEBUG]`, ...args);
    }
  },
  
  // Error log - always shown in development/production, but controlled in tests
  error: (prefix, ...args) => {
    if (!isTest || (isTest && window.__LOG_CONFIG?.ENABLE_ERROR_LOGS)) {
      console.error(`[${prefix}][ERROR]`, ...args);
    }
  },
  
  // Trace log with stack trace - disabled by default but available for debugging
  trace: (prefix, ...args) => {
    // Temporarily disable trace logs to reduce console noise
    // To re-enable, uncomment the code below
    // if (isDevelopment && !isTest) {
    //   console.log(`[${prefix}][TRACE]`, ...args);
    //   console.trace();
    // }
  },
  
  // StoryView-specific log
  storyView: (message) => {
    const isImportant = message.includes('Title updated') || message.includes('Auto-navigating');
    
    if (!isTest || (isTest && window.__LOG_CONFIG?.ENABLE_SERVICE_LOGS && isImportant)) {
      console.log(`[StoryView]${isDevelopment && !isImportant ? '[DEBUG]' : ''}`, message);
    }
  },
  
  // WebSocket-specific log
  ws: (message, isEssential = false) => {
    if (!isTest || (isTest && window.__LOG_CONFIG?.ENABLE_SERVICE_LOGS && isEssential)) {
      console.log(`[WebSocket]`, message);
    }
  },
  
  // ReadAloud-specific log
  readAloud: (message, isEssential = false) => {
    if (!isTest || (isTest && window.__LOG_CONFIG?.ENABLE_SERVICE_LOGS && isEssential)) {
      console.log(`[ReadAloud]`, message);
    }
  },
  
  // Audio-specific log with emoji
  audio: (message, isCritical = false) => {
    if (!isTest || (isTest && window.__LOG_CONFIG?.ENABLE_SERVICE_LOGS && isCritical)) {
      console.log(`🔊 [AudioService]`, message);
    }
  }
};

/**
 * Gets the actual base URL (domain only) for the current environment
 */
export const getBaseUrl = () => {
  // Production environment
  if (process.env.REACT_APP_ENV === 'production') {
    return 'https://gideonwald.com';
  } else {
    // Get URL from the current page
    const currentUrl = window.location;
    const protocol = currentUrl.protocol;
    const hostname = currentUrl.hostname;
    const port = currentUrl.port || (protocol === 'https:' ? '443' : '80');
    
    // Use the same server that served the page (domain only)
    return `${protocol}//${hostname}:${port}`;
  }
};

/**
 * Gets the URL for Weavers World API endpoints
 */
export const getWeaversWorldApiUrl = () => {
  return `${getBaseUrl()}/weaversworld/api`;
};

/**
 * Gets the URL for authentication API endpoints
 */
export const getAuthApiUrl = () => {
  return getBaseUrl();
};

/**
 * Calculates WebSocket URL based on current environment, including auth parameters if available
 * 
 * @param {Object} authParams - Optional authentication parameters
 * @param {string|number} authParams.userId - The authenticated user ID
 * @param {string|number} authParams.profileId - The active child profile ID
 * @returns {string} WebSocket URL with optional auth parameters
 */
export const getWebSocketUrl = (authParams) => {
  // Use window.location to get the current server information
  const currentUrl = window.location;
  const protocol = currentUrl.protocol === 'https:' ? 'wss' : 'ws';
  const hostname = currentUrl.hostname;
  const port = currentUrl.port || (currentUrl.protocol === 'https:' ? '443' : '80');
  
  // Base WebSocket URL
  const baseWsUrl = process.env.REACT_APP_ENV === 'production'
    ? 'wss://gideonwald.com/ws/weaversworld/'
    : `${protocol}://${hostname}:${port}/ws/weaversworld/`;
  
  // If no auth params provided, return base URL
  if (!authParams || (!authParams.userId && !authParams.profileId)) {
    // Log only once in development mode
    if (isDevelopment && !window._wsUrlLogged) {
      logger.info('WS', `Using WebSocket URL: ${baseWsUrl}`);
      window._wsUrlLogged = true;
    }
    return baseWsUrl;
  }
  
  // Add authentication parameters if provided
  const params = new URLSearchParams();
  if (authParams.userId) {
    params.append('user_id', authParams.userId);
  }
  if (authParams.profileId) {
    params.append('profile_id', authParams.profileId);
  }
  
  const wsUrl = `${baseWsUrl}?${params.toString()}`;
  
  // Log authenticated URL with parameters
  if (isDevelopment) {
    logger.info('WS', `Using authenticated WebSocket URL with parameters:`, {
      url: wsUrl,
      userId: authParams.userId || 'not provided',
      profileId: authParams.profileId || 'not provided'
    });
  }
  
  return wsUrl;
};

/**
 * Makes an API request with proper error handling
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Fetch options
 * @param {boolean} useAPIPrefix - Whether to use the API prefix (default: true)
 * @returns {Promise<any>} - Response data
 */
export const apiRequest = async (endpoint, options = {}, useAPIPrefix = true) => {
  // Properly prefix the URL with the weaversworld path
  const baseUrl = getBaseUrl();
  const prefix = useAPIPrefix ? '/weaversworld/api' : '/weaversworld';
  const formattedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  // Parse endpoint URL to handle any existing query parameters
  let finalEndpoint = formattedEndpoint;
  let existingParams = new URLSearchParams();
  
  // Check if the endpoint already contains query parameters
  if (formattedEndpoint.includes('?')) {
    const [path, queryString] = formattedEndpoint.split('?');
    finalEndpoint = path;
    existingParams = new URLSearchParams(queryString);
  }
  
  // Construct the complete URL
  let url = `${baseUrl}${prefix}${finalEndpoint}`;
  
  // If there are query parameters, add them to the URL
  if (existingParams.toString()) {
    url += `?${existingParams.toString()}`;
  }
  
  // Get method from options or default to GET
  const method = options.method ? options.method.toLowerCase() : 'get';
  
  // Add CSRF token to requests that modify data (POST, PUT, PATCH, DELETE)
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  // Add CSRF token for methods that modify data
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRFToken'] = csrfToken;
      logger.trace('API', 'Adding CSRF token to request');
    } else {
      logger.error('API', 'CSRF token not found in cookies');
    }
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include', // Important for cookies and CSRF
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    return await response.text();
  } catch (error) {
    logger.error('API', `Error during API request to ${url}:`, error);
    throw error;
  }
};